package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.Donation
import com.dotdrives.model.NewDonation
import com.dotdrives.repository.DonationRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.Instant

data class DonationDetailUiState(
    val loading: Boolean = false,
    val donation: Donation? = null,
    val reversing: Boolean = false,
    val error: String? = null,
)

class DonationDetailController(
    private val repo: DonationRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val clock: () -> Instant = { Clock.System.now() },
) : DotController<DonationDetailUiState>(DonationDetailUiState(), scope) {
    private var generation = 0L
    private var openedId: String? = null

    fun open(donationId: String) {
        openedId = donationId
        val gen = nextGeneration()
        update { DonationDetailUiState(loading = true) }
        scope.launch {
            try {
                val result = repo.get(orgId, donationId)
                if (!isCurrent(gen)) return@launch
                result
                    .onSuccess { donation ->
                        update { if (isCurrent(gen)) it.copy(loading = false, donation = donation, error = null) else it }
                    }
                    .onFailure { publishError(it, gen) }
            } catch (error: Exception) {
                publishError(error, gen)
            }
        }
    }

    fun retry() {
        openedId?.let(::open)
    }

    /** Refund/correction: schema fully supports this (insert grant, self-referencing FK, the
     * `amount < 0` check), and the detail panel is where the design puts "Refund…" -- but
     * this codebase had nothing that could ever write a reversal row before. A reversal of a
     * reversal, or of a non-positive row generally, is refused rather than offered. */
    fun reverse() {
        val current = state.value
        val original = current.donation ?: return
        if (current.reversing || !original.isActive || original.amount <= 0.0) return
        val newDonation = NewDonation(
            giverId = original.giverId,
            amount = -original.amount,
            driveId = original.driveId,
            pledgeId = original.pledgeId,
            targetId = original.targetId,
            paymentType = original.paymentType,
            source = "manual",
            memo = "Reversal of donation ${original.id}",
            occurredAt = clock(),
            reversesDonationId = original.id,
        )
        update { it.copy(reversing = true, error = null) }
        scope.launch {
            repo.record(orgId, newDonation)
                .onSuccess { reversal ->
                    if (openedId != original.id) return@onSuccess
                    update { it.copy(reversing = false, donation = reversal, error = null) }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    if (openedId != original.id) return@onFailure
                    update { it.copy(reversing = false, error = error.message ?: "Something went wrong") }
                }
        }
    }

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        update { if (isCurrent(generation)) it.copy(loading = false, error = error.message ?: "Something went wrong") else it }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(generation: Long): Boolean = generation == this.generation
}
